import { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { P, Gap, LinkButton, SvgIcon, List, theme, Toggle } from '@salecycle/ui-component-library';
import { parseExitIntentAreas } from '../../../utils';
import { useTrigger } from '../../hooks/useTrigger';
import { ConfigurationOption } from '../../../components/ConfigurationOption';
import { ExitIntentAreas } from 'sections/onsite-campaigns/types';
import { InfoTooltip } from 'sections/onsite-campaigns/pages/CampaignEditor/components/InfoTooltip';
import { useTranslation } from 'react-i18next';

const maxExitIntents = Object.keys(ExitIntentAreas).length;

const SpecificEdges = styled.div`
  display: flex;
  flex-direction: row;
`;

export function ExitIntentConfiguration({
  setExitIntentModalIsOpen,
}: {
  setExitIntentModalIsOpen: (val: boolean) => void;
}) {
  const { t } = useTranslation('campaigns');
  const { exitIntentAreas, selectAllExitIntents, unselectAllExitIntent } = useTrigger();

  useEffect(() => {
    if (exitIntentAreas?.length === maxExitIntents) {
      setChecked(true);
    }
  }, [exitIntentAreas?.length]);

  const defaultEdgeSelection = useMemo(() => {
    if (!exitIntentAreas) {
      return false;
    }

    if (exitIntentAreas?.length === maxExitIntents) {
      return true;
    }

    if (exitIntentAreas?.length) {
      return false;
    }

    return false;
  }, [exitIntentAreas]);

  const [allSelected, setChecked] = useState<boolean>(defaultEdgeSelection);

  const openExitIntentModal = useCallback(() => setExitIntentModalIsOpen(true), [setExitIntentModalIsOpen]);

  function toggleClicked() {
    setChecked(!allSelected);
    if (!allSelected) {
      selectAllExitIntents();
    } else {
      unselectAllExitIntent();
    }
  }

  return (
    <ConfigurationOption>
      <P>
        Which areas of the browser should activate the exit intent event?{' '}
        <InfoTooltip
          displayInline
          title={t('onsite.campaignExitIntent.tooltipTitle')}
          content={t('onsite.campaignExitIntent.tooltipContent')}
        />
      </P>
      <Gap bottom={16} />
      <Toggle ariaLabel="All edges" label="All edges" onChange={toggleClicked} checked={allSelected} />
      {/* negative margin when selected as despite being hardcoded to have 0 margin somehow there is a margin added */}
      {allSelected && <div style={{ margin: '-12px' }} />}
      {!allSelected && (
        <div style={{ marginTop: '-16px' }}>
          <SpecificEdges>
            <P>Specific edges</P>
            <Gap right={8} />
            {exitIntentAreas?.length === 0 && (
              <>
                <Gap right={4} />
                <LinkButton onClick={openExitIntentModal} hoverUnderline>
                  <SvgIcon id="pen" name="pen" color={theme.vars.newColor.blue2} />
                  <Gap right={8} />
                  Select edges
                </LinkButton>
              </>
            )}

            {exitIntentAreas?.length > 0 && (
              <LinkButton onClick={openExitIntentModal} hoverUnderline>
                <SvgIcon id="pen" name="pen" color={theme.vars.newColor.blue2} />
                <Gap right={8} />
                Edit selected edges
              </LinkButton>
            )}
          </SpecificEdges>
        </div>
      )}
      {!allSelected &&
        exitIntentAreas?.length > 0 &&
        exitIntentAreas?.length !== Object.values(ExitIntentAreas).length && (
          <>
            <Gap bottom={8} />
            <List>
              {parseExitIntentAreas(exitIntentAreas ?? []).map((area, index) => {
                return <li key={index}>{area}</li>;
              })}
            </List>
          </>
        )}
    </ConfigurationOption>
  );
}
